import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Popover,
  ScrollArea,
  Badge,
  IconButton,
  TextArea,
  TextField,
  Card,
  Tooltip,
} from "@radix-ui/themes";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { useSidebarChat } from "@/hooks/useSidebarChat";
import {
  integrationIcons,
  integrationBrandColors,
} from "@/components/workflow/IntegrationSelector";
import { ResizeHandle } from "./ResizeHandle";
import {
  X,
  Plus,
  Send,
  History,
  Trash2,
  Copy,
  Check,
  KeyRound,
  Paperclip,
  FileText,
  CreditCard,
} from "lucide-react";

const AnythinkIcon = ({ size = 14 }: { size?: number }) => (
  <img
    src="/assets/anythink-icon-03.svg"
    alt="Anythink"
    width={size}
    height={size}
    className="anythink-icon"
    style={{ opacity: 0.9 }}
  />
);

import "highlight.js/styles/github.css";

interface AISidebarPanelProps {
  onClose: () => void;
  width: number;
  onWidthChange: (width: number) => void;
}

// Models are now sourced from useSidebarChat based on tenant AI config

// ── Code block with copy button ────────────────────────────────────────────
function CodeBlock({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const isBlock = className?.includes("language-");

  const handleCopy = () => {
    const text = typeof children === "string" ? children : "";
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isBlock) {
    return (
      <code
        style={{
          backgroundColor: "var(--gray-a3)",
          padding: "2px 5px",
          borderRadius: "3px",
          fontFamily: "var(--font-mono)",
          fontSize: "13px",
        }}
      >
        {children}
      </code>
    );
  }

  return (
    <Box style={{ position: "relative", margin: "8px 0" }}>
      <IconButton
        size="1"
        variant="ghost"
        onClick={handleCopy}
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 2,
          opacity: 0.7,
        }}
        title="Copy code"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </IconButton>
      <Box
        style={{
          backgroundColor: "var(--gray-a3)",
          padding: "12px",
          borderRadius: "6px",
          fontFamily: "var(--font-mono)",
          fontSize: "13px",
          overflow: "auto",
        }}
      >
        <code className={className}>{children}</code>
      </Box>
    </Box>
  );
}

// ── Relative time helper ───────────────────────────────────────────────────
function relativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
}

// ── Markdown components ────────────────────────────────────────────────────
const markdownComponents = {
  p: ({ children }: any) => (
    <Text size="2" style={{ margin: "0 0 8px 0", whiteSpace: "pre-wrap" }}>
      {children}
    </Text>
  ),
  h1: ({ children }: any) => (
    <Text
      size="4"
      weight="bold"
      style={{ margin: "12px 0 8px 0", display: "block" }}
    >
      {children}
    </Text>
  ),
  h2: ({ children }: any) => (
    <Text
      size="3"
      weight="bold"
      style={{ margin: "10px 0 6px 0", display: "block" }}
    >
      {children}
    </Text>
  ),
  h3: ({ children }: any) => (
    <Text
      size="2"
      weight="bold"
      style={{ margin: "8px 0 4px 0", display: "block" }}
    >
      {children}
    </Text>
  ),
  code: CodeBlock,
  ul: ({ children }: any) => (
    <ul style={{ margin: "8px 0", paddingLeft: "20px", listStyleType: "disc" }}>
      {children}
    </ul>
  ),
  ol: ({ children }: any) => (
    <ol
      style={{ margin: "8px 0", paddingLeft: "20px", listStyleType: "decimal" }}
    >
      {children}
    </ol>
  ),
  li: ({ children }: any) => (
    <li style={{ margin: "4px 0", display: "list-item" }}>{children}</li>
  ),
  blockquote: ({ children }: any) => (
    <Box
      style={{
        borderLeft: "3px solid var(--accent-9)",
        paddingLeft: "12px",
        margin: "8px 0",
        color: "var(--gray-11)",
        fontStyle: "italic",
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }: any) => <Text weight="bold">{children}</Text>,
  em: ({ children }: any) => (
    <Text style={{ fontStyle: "italic" }}>{children}</Text>
  ),
  table: ({ children }: any) => (
    <Box
      style={{
        margin: "12px 0",
        overflow: "auto",
        border: "1px solid var(--gray-6)",
        borderRadius: "6px",
      }}
    >
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}
      >
        {children}
      </table>
    </Box>
  ),
  thead: ({ children }: any) => (
    <thead
      style={{
        backgroundColor: "var(--gray-a3)",
        borderBottom: "2px solid var(--gray-6)",
      }}
    >
      {children}
    </thead>
  ),
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => (
    <tr style={{ borderBottom: "1px solid var(--gray-6)" }}>{children}</tr>
  ),
  th: ({ children }: any) => (
    <th
      style={{
        padding: "8px 12px",
        textAlign: "left" as const,
        fontWeight: "600",
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td style={{ padding: "8px 12px" }}>{children}</td>
  ),
  hr: () => (
    <Box
      style={{
        margin: "16px 0",
        height: "1px",
        backgroundColor: "var(--gray-6)",
      }}
    />
  ),
};

// ── Example prompts ────────────────────────────────────────────────────────
const EXAMPLE_PROMPTS = [
  "What entities do I have?",
  "Create a new workflow",
  "Show me my users",
];

// ── Main component ─────────────────────────────────────────────────────────
export function AISidebarPanel({
  onClose,
  width,
  onWidthChange,
}: AISidebarPanelProps) {
  const {
    messages,
    sendMessage,
    isLoading,
    error,
    selectedModel,
    setSelectedModel,
    availableModels,
    aiMode,
    messageAttachments,
    conversations,
    activeConversationId,
    selectConversation,
    newConversation,
    deleteConversation,
    isLoadingHistory,
    showHistory,
    setShowHistory,
    sessionUsage,
  } = useSidebarChat();

  const [isResizing, setIsResizing] = useState(false);
  const [input, setInput] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<
    Array<{ file: File; base64: string; mediaType: string }>
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [modelProviderFilter, setModelProviderFilter] = useState<string | null>(
    null,
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Resize handling
  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      onWidthChange(
        Math.max(320, Math.min(800, window.innerWidth - e.clientX)),
      );
    };
    const handleMouseUp = () => setIsResizing(false);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, onWidthChange]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && pendingAttachments.length === 0) || isLoading) return;
    const text = input.trim();
    const attachments =
      pendingAttachments.length > 0
        ? pendingAttachments.map((a) => ({
            mediaType: a.mediaType,
            base64Data: a.base64,
            fileName: a.file.name,
          }))
        : undefined;
    setInput("");
    setPendingAttachments([]);
    sendMessage(text, attachments);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];

    for (const file of Array.from(files)) {
      if (file.size > maxSize) continue;
      if (!allowedTypes.includes(file.type)) continue;
      if (pendingAttachments.length >= 5) break;

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]); // Strip data:... prefix
        };
        reader.readAsDataURL(file);
      });

      setPendingAttachments((prev) => [
        ...prev,
        { file, base64, mediaType: file.type },
      ]);
    }

    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const addFiles = async (files: FileList | File[]) => {
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    const validFiles = Array.from(files).filter(
      (f) => f.size <= maxSize && allowedTypes.includes(f.type),
    );

    const newAttachments: Array<{
      file: File;
      base64: string;
      mediaType: string;
    }> = [];
    for (const file of validFiles) {
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.readAsDataURL(file);
      });
      newAttachments.push({ file, base64, mediaType: file.type });
    }

    if (newAttachments.length > 0) {
      setPendingAttachments((prev) => [...prev, ...newAttachments].slice(0, 5));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as React.FormEvent);
    }
  };

  const getMessageText = (message: (typeof messages)[0]): string => {
    return (
      message.parts
        ?.filter((p): p is { type: "text"; text: string } => p.type === "text")
        .map((p) => p.text)
        .join("") ?? ""
    );
  };

  return (
    <Box
      style={{
        width: `${width}px`,
        minWidth: "320px",
        maxWidth: "800px",
        height: "100%",
        position: "relative",
        flexShrink: 0,
        borderLeft: "1px solid var(--gray-5)",
        backgroundColor: "var(--sidebar-bg, var(--color-background))",
      }}
    >
      <ResizeHandle onMouseDown={() => setIsResizing(true)} />

      <Flex direction="column" style={{ height: "100%" }}>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <Flex
          align="center"
          justify="between"
          px="3"
          style={{
            height: "4rem",
            borderBottom: "1px solid var(--gray-5)",
            flexShrink: 0,
          }}
        >
          <Flex align="center" gap="2">
            {(() => {
              // Show provider icon based on the selected model's provider
              const currentModel = availableModels.find(
                (m) => m.id === selectedModel,
              );
              const provider = currentModel?.provider || "claude";
              const ProvIcon = integrationIcons[provider];
              const brand = integrationBrandColors[provider];
              if (aiMode === "platform") {
                return <AnythinkIcon size={20} />;
              }
              return ProvIcon ? (
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "5px",
                    backgroundColor: brand?.bg || "var(--gray-8)",
                  }}
                >
                  <Box
                    style={{
                      width: 14,
                      height: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ProvIcon />
                  </Box>
                </Flex>
              ) : (
                <AnythinkIcon size={20} />
              );
            })()}
            <Text size="3" weight="bold">
              {aiMode === "platform" ? "Anythink AI" : "AI Chat"}
            </Text>
            {activeConversationId && (
              <Badge size="1" variant="soft" color="gray">
                {conversations
                  .find((c) => c.id === activeConversationId)
                  ?.name?.slice(0, 20) || "Chat"}
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap="1">
            <Tooltip content="Chat history">
              <IconButton
                size="1"
                variant={showHistory ? "solid" : "ghost"}
                onClick={() => setShowHistory(!showHistory)}
              >
                <History size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip content="New conversation">
              <IconButton size="1" variant="ghost" onClick={newConversation}>
                <Plus size={16} />
              </IconButton>
            </Tooltip>
            <IconButton size="1" variant="ghost" onClick={onClose}>
              <X size={16} />
            </IconButton>
          </Flex>
        </Flex>

        {/* ── Conversation History (collapsible) ──────────────────────── */}
        <Box
          style={{
            maxHeight: showHistory ? "240px" : "0px",
            overflow: "hidden",
            transition: "max-height 200ms ease",
            borderBottom: showHistory ? "1px solid var(--gray-5)" : "none",
          }}
        >
          <ScrollArea style={{ maxHeight: "240px" }}>
            <Box p="2">
              {conversations.map((convo) => (
                <Card
                  key={convo.id}
                  style={{
                    padding: "10px 12px",
                    marginBottom: "4px",
                    cursor: "pointer",
                    backgroundColor:
                      activeConversationId === convo.id
                        ? "var(--accent-3)"
                        : "transparent",
                    border:
                      activeConversationId === convo.id
                        ? "1px solid var(--accent-6)"
                        : "1px solid transparent",
                  }}
                  onClick={() => selectConversation(convo.id)}
                >
                  <Flex justify="between" align="start">
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="2"
                        weight="medium"
                        truncate
                        style={{ display: "block" }}
                      >
                        {convo.name || "Untitled"}
                      </Text>
                      <Text size="1" color="gray">
                        {relativeTime(convo.updated_at)}
                      </Text>
                    </Box>
                    <IconButton
                      size="1"
                      variant="ghost"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(convo.id);
                      }}
                      style={{ opacity: 0.5, flexShrink: 0 }}
                    >
                      <Trash2 size={12} />
                    </IconButton>
                  </Flex>
                </Card>
              ))}
              {conversations.length === 0 && (
                <Flex direction="column" align="center" py="4">
                  <Text size="2" color="gray">
                    No conversations yet
                  </Text>
                </Flex>
              )}
            </Box>
          </ScrollArea>
        </Box>

        {/* ── Messages (drag-and-drop zone) ────────────────────────── */}
        <ScrollArea
          className="grid-bg"
          style={{
            flex: 1,
            position: "relative",
            backgroundColor: "var(--content-bg)",
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
            setIsDragging(false);
          }}
          onDrop={handleDrop}
        >
          {isDragging && (
            <Flex
              align="center"
              justify="center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                backgroundColor: "var(--accent-a3)",
                border: "2px dashed var(--accent-9)",
                borderRadius: "var(--radius-3)",
                pointerEvents: "all",
              }}
            >
              <Text size="3" weight="medium" color="orange">
                Drop files here
              </Text>
            </Flex>
          )}
          <Box p="3">
            {/* Empty state */}
            {messages.length === 0 && !isLoadingHistory && (
              <Flex
                direction="column"
                align="center"
                justify="center"
                style={{ minHeight: "300px" }}
              >
                {aiMode === "platform" ? (
                  <AnythinkIcon size={40} />
                ) : (
                  (() => {
                    const currentModel = availableModels.find(
                      (m) => m.id === selectedModel,
                    );
                    const provider = currentModel?.provider || "claude";
                    const ProvIcon = integrationIcons[provider];
                    const brand = integrationBrandColors[provider];
                    return ProvIcon ? (
                      <Flex
                        align="center"
                        justify="center"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "10px",
                          backgroundColor: brand?.bg || "var(--gray-8)",
                        }}
                      >
                        <Box
                          style={{
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ProvIcon />
                        </Box>
                      </Flex>
                    ) : (
                      <AnythinkIcon size={40} />
                    );
                  })()
                )}
                <Text size="3" weight="bold" mt="3" mb="1">
                  {aiMode === "platform" ? "Anythink AI" : "AI Chat"}
                </Text>
                <Text
                  size="2"
                  color="gray"
                  mb="3"
                  style={{ textAlign: "center" }}
                >
                  Manage your database, entities, workflows, and more.
                </Text>
                <Flex
                  direction="column"
                  gap="2"
                  style={{ width: "100%", maxWidth: "280px" }}
                >
                  {EXAMPLE_PROMPTS.map((prompt) => (
                    <Box
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      style={{
                        padding: "10px 14px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        backgroundColor: "var(--accent-a3)",
                        color: "var(--accent-a11)",
                        transition: "background-color 150ms",
                      }}
                      className="ai-prompt-card hover:bg-accent-4"
                    >
                      <Text size="2">"{prompt}"</Text>
                    </Box>
                  ))}
                </Flex>
              </Flex>
            )}

            {isLoadingHistory && (
              <Flex align="center" justify="center" py="6">
                <Text size="2" color="gray">
                  Loading conversation...
                </Text>
              </Flex>
            )}

            {/* Messages */}
            {messages.map((message, index) => {
              const isUser = message.role === "user";
              const text = getMessageText(message);
              const timestamp = (message as any).createdAt;

              return (
                <Flex
                  key={message.id || index}
                  direction="column"
                  align={isUser ? "end" : "start"}
                  mb="3"
                  style={{
                    width: "100%",
                    animation: "fadeInUp 0.2s ease",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    className="ai-bubble"
                    style={{
                      maxWidth: "85%",
                      minWidth: "100px",
                      wordBreak: "break-word",
                      borderRadius: isUser
                        ? "16px 16px 4px 16px"
                        : "16px 16px 16px 4px",
                      backgroundColor: isUser
                        ? "var(--accent-9)"
                        : "var(--gray-a3)",
                      color: isUser ? "white" : "var(--color-text)",
                      padding: "10px 14px",
                      border: isUser ? "none" : "1px solid var(--gray-a4)",
                    }}
                  >
                    {isUser ? (
                      <>
                        {(() => {
                          const atts = messageAttachments?.get(text);
                          if (!atts || atts.length === 0) return null;
                          return (
                            <Flex gap="2" mb="2" wrap="wrap">
                              {atts.map((att, i) =>
                                att.mediaType.startsWith("image/") ? (
                                  <img
                                    key={i}
                                    src={`data:${att.mediaType};base64,${att.base64Data}`}
                                    alt={att.fileName || "attachment"}
                                    style={{
                                      maxWidth: "200px",
                                      maxHeight: "150px",
                                      borderRadius: "8px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <Flex
                                    key={i}
                                    align="center"
                                    gap="1"
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor: "rgba(255,255,255,0.15)",
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <FileText size={14} />
                                    <Text size="1">
                                      {att.fileName || "document.pdf"}
                                    </Text>
                                  </Flex>
                                ),
                              )}
                            </Flex>
                          );
                        })()}
                        <Text
                          size="2"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {text}
                        </Text>
                      </>
                    ) : (
                      <Box style={{ fontSize: "14px", lineHeight: "1.5" }}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeHighlight]}
                          components={markdownComponents as any}
                        >
                          {text}
                        </ReactMarkdown>
                      </Box>
                    )}
                  </Box>
                  {timestamp && (
                    <Text size="1" color="gray" mt="1" style={{ opacity: 0.6 }}>
                      {relativeTime(timestamp)}
                    </Text>
                  )}
                </Flex>
              );
            })}

            {/* Loading indicator */}
            {isLoading &&
              (messages.length === 0 ||
                messages[messages.length - 1]?.role !== "assistant") && (
                <Flex
                  direction="column"
                  align="start"
                  mb="3"
                  style={{ animation: "fadeInUp 0.2s ease" }}
                >
                  <Box
                    className="ai-bubble"
                    style={{
                      borderRadius: "16px 16px 16px 4px",
                      backgroundColor: "var(--gray-a3)",
                      padding: "10px 14px",
                      border: "1px solid var(--gray-a4)",
                    }}
                  >
                    <Flex align="center" gap="2">
                      {(() => {
                        if (aiMode === "platform")
                          return <AnythinkIcon size={14} />;
                        const currentModel = availableModels.find(
                          (m) => m.id === selectedModel,
                        );
                        const provider = currentModel?.provider || "claude";
                        const ProvIcon = integrationIcons[provider];
                        const brand = integrationBrandColors[provider];
                        return ProvIcon ? (
                          <Flex
                            align="center"
                            justify="center"
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: "4px",
                              backgroundColor: brand?.bg || "var(--gray-8)",
                            }}
                          >
                            <Box
                              style={{
                                width: 12,
                                height: 12,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ProvIcon />
                            </Box>
                          </Flex>
                        ) : (
                          <AnythinkIcon size={14} />
                        );
                      })()}
                      <Text size="2" color="gray">
                        <span className="bouncing-dots">
                          <span className="dot" />
                          <span className="dot" />
                          <span className="dot" />
                        </span>
                      </Text>
                    </Flex>
                  </Box>
                </Flex>
              )}

            {error && (
              <Flex direction="column" align="start" mb="3">
                <Box
                  style={{
                    borderRadius: "16px 16px 16px 4px",
                    backgroundColor:
                      (error as any).code === "QUOTA_EXCEEDED"
                        ? "var(--orange-a3)"
                        : "var(--red-a3)",
                    padding: "10px 14px",
                    border:
                      (error as any).code === "QUOTA_EXCEEDED"
                        ? "1px solid var(--orange-a6)"
                        : "1px solid var(--red-a6)",
                    maxWidth: "90%",
                  }}
                >
                  <Flex direction="column" gap="2">
                    <Flex align="center" gap="2">
                      {(error as any).code === "QUOTA_EXCEEDED" ? (
                        <CreditCard size={14} color="var(--orange-11)" />
                      ) : null}
                      <Text
                        size="2"
                        color={
                          (error as any).code === "QUOTA_EXCEEDED"
                            ? "orange"
                            : "red"
                        }
                        weight="medium"
                      >
                        {(error as any).code === "QUOTA_EXCEEDED"
                          ? "Quota Limit Reached"
                          : "Something went wrong"}
                      </Text>
                    </Flex>

                    <Text size="2" color="gray">
                      {error.message || "Please try again later."}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            )}

            <div ref={messagesEndRef} />
          </Box>
        </ScrollArea>

        {/* ── Input ───────────────────────────────────────────────────── */}
        <Box
          p="3"
          style={{ borderTop: "1px solid var(--gray-5)", flexShrink: 0 }}
        >
          {/* Attachment previews */}
          {pendingAttachments.length > 0 && (
            <Flex gap="2" mb="2" wrap="wrap">
              {pendingAttachments.map((att, i) => (
                <Box
                  key={i}
                  style={{
                    position: "relative",
                    borderRadius: "var(--radius-2)",
                    border: "1px solid var(--gray-5)",
                    overflow: "hidden",
                  }}
                >
                  {att.mediaType.startsWith("image/") ? (
                    <img
                      src={`data:${att.mediaType};base64,${att.base64}`}
                      alt={att.file.name}
                      style={{
                        width: 56,
                        height: 56,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Flex
                      align="center"
                      gap="1"
                      p="2"
                      style={{ height: 56, backgroundColor: "var(--gray-a2)" }}
                    >
                      <FileText size={16} color="var(--red-9)" />
                      <Text
                        size="1"
                        color="gray"
                        style={{
                          maxWidth: 80,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {att.file.name}
                      </Text>
                    </Flex>
                  )}
                  <IconButton
                    size="1"
                    variant="solid"
                    color="gray"
                    onClick={() =>
                      setPendingAttachments((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                    }}
                  >
                    <X size={10} />
                  </IconButton>
                </Box>
              ))}
            </Flex>
          )}

          <form onSubmit={handleSubmit}>
            <Flex gap="2" align="end">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              <Tooltip content="Attach image or PDF">
                <IconButton
                  type="button"
                  variant="ghost"
                  size="3"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading || pendingAttachments.length >= 5}
                >
                  <Paperclip size={16} />
                </IconButton>
              </Tooltip>
              <Box style={{ flex: 1 }}>
                <TextArea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything..."
                  disabled={isLoading}
                  style={{
                    minHeight: "44px",
                    maxHeight: "120px",
                    resize: "none",
                  }}
                />
              </Box>
              <IconButton
                type="submit"
                disabled={
                  (!input.trim() && pendingAttachments.length === 0) ||
                  isLoading
                }
                variant="solid"
                size="3"
              >
                <Send size={16} />
              </IconButton>
            </Flex>
          </form>

          <Flex align="center" gap="2" mt="2" justify="between">
            <Flex align="center" gap="2">
              <Tooltip
                content={
                  aiMode === "byok"
                    ? "Using your own API key"
                    : "Using Anythink AI credits"
                }
              >
                {aiMode === "byok" ? (
                  <KeyRound size={14} color="var(--green-9)" />
                ) : (
                  <AnythinkIcon />
                )}
              </Tooltip>
              {(() => {
                const currentModel = availableModels.find(
                  (m) => m.id === selectedModel,
                );
                const CurrentIcon = currentModel
                  ? integrationIcons[currentModel.provider]
                  : null;
                const currentBrand = currentModel
                  ? integrationBrandColors[currentModel.provider]
                  : null;
                return (
                  <Popover.Root>
                    <Popover.Trigger>
                      <Flex
                        align="center"
                        gap="2"
                        style={{
                          cursor: "pointer",
                          padding: "2px 8px",
                          borderRadius: "var(--radius-2)",
                          border: "1px solid var(--gray-6)",
                        }}
                      >
                        {CurrentIcon && (
                          <Flex
                            align="center"
                            justify="center"
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: "3px",
                              backgroundColor:
                                currentBrand?.bg || "var(--gray-8)",
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              style={{
                                width: 10,
                                height: 10,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CurrentIcon />
                            </Box>
                          </Flex>
                        )}
                        <Text size="2">{currentModel?.badge || "Model"}</Text>
                        <Text size="2">
                          {currentModel?.name || selectedModel}
                        </Text>
                        <Text size="1" color="gray">
                          ▾
                        </Text>
                      </Flex>
                    </Popover.Trigger>
                    <Popover.Content
                      style={{ width: 340, padding: 0, zIndex: 12000 }}
                      container={document.body}
                    >
                      {/* Provider filter tabs */}
                      {(() => {
                        const providers = [
                          ...new Set(availableModels.map((m) => m.provider)),
                        ];
                        return providers.length > 1 ? (
                          <Flex
                            gap="0"
                            style={{
                              borderBottom: "1px solid var(--gray-5)",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              onClick={() => setModelProviderFilter(null)}
                              style={{
                                padding: "6px 10px",
                                cursor: "pointer",
                                fontSize: "12px",
                                fontWeight:
                                  modelProviderFilter === null ? 600 : 400,
                                borderBottom:
                                  modelProviderFilter === null
                                    ? "2px solid var(--accent-9)"
                                    : "2px solid transparent",
                                color:
                                  modelProviderFilter === null
                                    ? "var(--accent-11)"
                                    : "var(--gray-11)",
                              }}
                            >
                              All
                            </Box>
                            {providers.map((p) => {
                              const ProvIcon = integrationIcons[p];
                              const brand = integrationBrandColors[p];
                              return (
                                <Flex
                                  key={p}
                                  align="center"
                                  gap="1"
                                  onClick={() => setModelProviderFilter(p)}
                                  style={{
                                    padding: "6px 10px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight:
                                      modelProviderFilter === p ? 600 : 400,
                                    borderBottom:
                                      modelProviderFilter === p
                                        ? `2px solid ${brand?.bg || "var(--accent-9)"}`
                                        : "2px solid transparent",
                                    color:
                                      modelProviderFilter === p
                                        ? "var(--accent-11)"
                                        : "var(--gray-11)",
                                  }}
                                >
                                  {ProvIcon && (
                                    <Flex
                                      align="center"
                                      justify="center"
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "3px",
                                        backgroundColor:
                                          brand?.bg || "var(--gray-8)",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Box
                                        style={{
                                          width: 9,
                                          height: 9,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <ProvIcon />
                                      </Box>
                                    </Flex>
                                  )}
                                  {p.charAt(0).toUpperCase() + p.slice(1)}
                                </Flex>
                              );
                            })}
                          </Flex>
                        ) : null;
                      })()}
                      {/* Search */}
                      <Box
                        p="2"
                        style={{ borderBottom: "1px solid var(--gray-5)" }}
                      >
                        <TextField.Root
                          placeholder="Search models..."
                          size="1"
                          id="model-search"
                          onChange={(e) => {
                            const el = e.target;
                            el.setAttribute(
                              "data-search",
                              el.value.toLowerCase(),
                            );
                            el.closest("[data-radix-popper-content-wrapper]")
                              ?.querySelectorAll("[data-model-item]")
                              .forEach((item: any) => {
                                item.style.display =
                                  item.dataset.modelItem.includes(
                                    el.value.toLowerCase(),
                                  )
                                    ? ""
                                    : "none";
                              });
                          }}
                        />
                      </Box>
                      {/* Model list */}
                      <ScrollArea style={{ maxHeight: 280 }}>
                        <Flex direction="column" p="1" gap="0">
                          {availableModels
                            .filter(
                              (model) =>
                                !modelProviderFilter ||
                                model.provider === modelProviderFilter,
                            )
                            .map((model) => {
                              const ProvIcon = integrationIcons[model.provider];
                              const brand =
                                integrationBrandColors[model.provider];
                              const isSelected = model.id === selectedModel;
                              return (
                                <Popover.Close key={model.id}>
                                  <Box
                                    data-model-item={`${model.name.toLowerCase()} ${model.provider} ${model.badge.toLowerCase()}`}
                                    onClick={() => setSelectedModel(model.id)}
                                    style={{
                                      padding: "6px 8px",
                                      borderRadius: "var(--radius-2)",
                                      cursor: "pointer",
                                      backgroundColor: isSelected
                                        ? "var(--accent-a3)"
                                        : "transparent",
                                    }}
                                  >
                                    <Flex align="center" gap="2">
                                      {ProvIcon && (
                                        <Flex
                                          align="center"
                                          justify="center"
                                          style={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: "4px",
                                            backgroundColor:
                                              brand?.bg || "var(--gray-8)",
                                            flexShrink: 0,
                                          }}
                                        >
                                          <Box
                                            style={{
                                              width: 12,
                                              height: 12,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <ProvIcon />
                                          </Box>
                                        </Flex>
                                      )}
                                      <Badge size="1" variant="soft">
                                        {model.badge}
                                      </Badge>
                                      <Text
                                        size="2"
                                        weight={
                                          isSelected ? "medium" : "regular"
                                        }
                                        style={{ flex: 1 }}
                                      >
                                        {model.name}
                                      </Text>
                                      {model.multiplier !== undefined &&
                                        model.multiplier !== 1 && (
                                          <Text
                                            size="1"
                                            color={
                                              model.multiplier > 1
                                                ? "red"
                                                : "green"
                                            }
                                            weight="bold"
                                          >
                                            {model.multiplier}x
                                          </Text>
                                        )}
                                    </Flex>
                                  </Box>
                                </Popover.Close>
                              );
                            })}
                        </Flex>
                      </ScrollArea>
                    </Popover.Content>
                  </Popover.Root>
                );
              })()}
            </Flex>
            <Flex align="center" gap="2">
              {sessionUsage.total_tokens > 0 && (
                <Tooltip
                  content={`Input: ${sessionUsage.input_tokens.toLocaleString()} · Output: ${sessionUsage.output_tokens.toLocaleString()}`}
                >
                  <Badge
                    size="1"
                    variant="soft"
                    color={aiMode === "byok" ? "green" : "gray"}
                  >
                    {sessionUsage.total_tokens.toLocaleString()} tokens
                  </Badge>
                </Tooltip>
              )}
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
